/**
 * Registers axios and fetch interceptors to log all API calls to apiLogStore.
 * Import this once at app entry (index.js).
 */
import axios from "axios";
import { addLog, getEndpointAndEnv } from "./apiLogStore";
import { refreshAgentCache, shouldRefreshOnApiRequest } from "./refreshAgentCache";

function safeStringify(obj) {
  try {
    if (obj === undefined) return "";
    if (typeof obj === "string") return obj;
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
}

// --- Axios interceptors ---
axios.interceptors.request.use(
  (config) => {
    const url = config.url?.startsWith("http") ? config.url : (config.baseURL || "") + config.url;
    const { endpoint, env } = getEndpointAndEnv(url);
    config._logMeta = {
      endpoint,
      env,
      method: (config.method || "get").toUpperCase(),
      payload: config.data,
    };

    if (!config._skipAgentRefresh && shouldRefreshOnApiRequest(config.method, url)) {
      // Fire-and-forget so original API call is not delayed.
      void refreshAgentCache();
    }

    return config;
  },
  (err) => Promise.reject(err)
);

axios.interceptors.response.use(
  (response) => {
    const meta = response.config._logMeta;
    if (meta) {
      addLog({
        ...meta,
        status: response.status,
        success: true,
        message: "Request completed successfully.",
        responsePreview: response.data != null ? safeStringify(response.data).slice(0, 500) : "",
      });
    }
    return response;
  },
  (error) => {
    const meta = error.config?._logMeta;
    const endpoint = meta?.endpoint ?? (error.config?.url || "?");
    const env = meta?.env ?? "unknown";
    const method = meta?.method ?? "?";
    const payload = meta?.payload;
    const status = error.response?.status;
    const data = error.response?.data;
    let message = "Request failed.";
    if (status) message += ` Server returned ${status}.`;
    if (data?.message) message += ` ${data.message}`;
    else if (data?.error) message += ` ${data.error}`;
    else if (typeof data === "string") message += ` ${data}`;
    addLog({
      endpoint,
      env,
      method,
      payload,
      status: status || "—",
      success: false,
      message: message.trim(),
      responsePreview: data != null ? safeStringify(data).slice(0, 500) : (error.message || ""),
    });
    return Promise.reject(error);
  }
);

// --- Fetch patch (for Login and any other fetch calls) ---
const originalFetch = window.fetch;
window.fetch = function (...args) {
  const [urlOrReq, opts = {}] = args;
  const url = typeof urlOrReq === "string" ? urlOrReq : urlOrReq?.url;
  const method = (opts.method || "GET").toUpperCase();
  const { endpoint, env } = getEndpointAndEnv(url);
  let bodyPayload = opts.body;
  try {
    if (bodyPayload && typeof bodyPayload === "string") bodyPayload = JSON.parse(bodyPayload);
  } catch (_) {}

  return originalFetch.apply(this, args).then(
    (res) => {
      const clone = res.clone();
      clone.text().then((text) => {
        let responsePreview = "";
        try {
          responsePreview = JSON.stringify(JSON.parse(text), null, 2).slice(0, 500);
        } catch {
          responsePreview = text.slice(0, 500);
        }
        addLog({
          endpoint,
          env,
          method,
          payload: bodyPayload,
          status: res.status,
          success: res.ok,
          message: res.ok ? "Request completed successfully." : `Request failed. Server returned ${res.status}.`,
          responsePreview,
        });
      });
      return res;
    },
    (err) => {
      addLog({
        endpoint,
        env,
        method,
        payload: bodyPayload,
        status: "—",
        success: false,
        message: err?.message || "Request failed (network or other error).",
        responsePreview: "",
      });
      throw err;
    }
  );
};
